import { HttpClient, HttpXsrfTokenExtractor } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { LogService } from 'shared/core/data-access/log';
import { AbstractPermissionService } from 'shared/core/data-access/permission';
import {
    AbstractUserInfoService,
    UserInfo,
} from 'shared/core/data-access/user-info';
import { WINDOW } from 'shared/framework/data-access/angular';
import { submitHiddenForm } from 'shared/framework/util/javascript';
import { LoginOption } from '../../models/login-option/login-option.model';
import { AUTH_CONFIG } from '../../providers/auth-config/auth-config.token';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    #http = inject(HttpClient);
    #logService = inject(LogService);
    #userInfoService = inject(AbstractUserInfoService, { optional: true });
    #permissionService = inject(AbstractPermissionService, { optional: true });
    #tokenExtractor = inject(HttpXsrfTokenExtractor);
    #authConfig = inject(AUTH_CONFIG, { optional: true }) ?? {};
    #window = inject(WINDOW);

    #loginOptionsUri = `${this.#authConfig.basePath ?? ''}/login-options`;
    #logoutUri = `${this.#authConfig.basePath ?? ''}/logout`;

    userInfo?: UserInfo;
    loadingUserInfo = false;

    get csrfToken() {
        return this.#tokenExtractor.getToken();
    }

    init = () => {
        this.#logService.debug('[Auth] Init');
        return this.loadUserInfo().pipe(
            switchMap(this.#loadPermissionsForUser),
            switchMap((observable) => observable),
        );
    };

    #loadPermissionsForUser = async (
        userInfo: UserInfo | undefined,
    ): Promise<Observable<unknown>> => {
        if (!this.#permissionService) {
            return of(undefined);
        }

        await this.#permissionService.init();

        return this.#permissionService.loadAllPermissions({
            ...userInfo,
        });
    };

    loadUserInfo = () => {
        this.#logService.debug('[Auth] Loading user');

        if (!this.#userInfoService) {
            return of(undefined);
        }

        this.loadingUserInfo = true;

        return this.#userInfoService.getUserInfo().pipe(
            tap((userInfo) => {
                if (userInfo) {
                    this.#logService.debug('[Auth] Loaded user successfully');
                } else {
                    this.#logService.debug('[Auth] No user found');
                }
                this.userInfo = userInfo;
            }),
            finalize(() => {
                this.loadingUserInfo = false;
            }),
        );
    };

    logout = () => {
        if (this.#authConfig.basePath) {
            this.#logService.debug('[Auth] Logging out');
            submitHiddenForm('post', this.#logoutUri, {
                _csrf: this.csrfToken,
            });
        } else {
            this.#logService.debug('[Auth] Logging out using old endpoint');
            this.#window.location.href = `/intouch-base/rest/nlogout`;
        }

        if (this.#permissionService) {
            this.#permissionService.clearPermissions();
        }
    };

    login = (loginUri?: string) => {
        if (this.#authConfig.registrationId || !loginUri) {
            this.#logService.debug('[Auth] Logging in');
            this.#window.location.href = this.#normalizeLoginUri(
                `/oauth2/authorization/${this.#authConfig.registrationId ?? ''}`,
            );
        } else {
            this.#logService.debug(
                '[Auth] Logging in with chosen login option',
            );
            this.#window.location.href = loginUri;
        }
    };

    loadLoginOptions = (): Observable<LoginOption[]> => {
        this.#logService.debug('[Auth] Loading login options');

        return this.#http.get<LoginOption[]>(this.#loginOptionsUri).pipe(
            map((loginOptions: LoginOption[]) =>
                loginOptions.map((loginOption) => ({
                    label: loginOption.label,
                    loginUri: this.#normalizeLoginUri(loginOption.loginUri),
                })),
            ),
        );
    };

    #normalizeLoginUri = (loginUri: string): string => {
        return loginUri.startsWith('/')
            ? `${this.#authConfig.basePath ?? ''}${loginUri}`
            : loginUri;
    };
}
